import { GraphQL } from '@guestgoodie/graphql'
import { useLingui } from '@lingui/react/macro'

import {
  ArrangeIcon,
  Configuration01Icon,
  Loading01Icon,
  Tick01Icon
} from '@hugeicons/core-solid-rounded'

import { extractOptions, extractString, Step } from '../types'

export function useConnectorSteps(
  provider: GraphQL.Providers,
  data?: Record<string, unknown>
): Step[] {
  const { t } = useLingui()

  const steps: Record<GraphQL.Providers, Step[]> = {
    [GraphQL.Providers.Oracle]: [
      {
        id: 'init',
        icon: Configuration01Icon,
        type: 'form',
        title: t`Initialize Oracle Integration`,
        description: t`First we need some information from your Oracle Cloud.`,
        fields: [
          {
            type: 'text',
            label: t`Hotel ID`,
            name: 'hotel_id',
            required: true
          },
          {
            type: 'text',
            label: t`Enterprise ID`,
            name: 'enterprise_id',
            required: false
          },
          {
            type: 'text',
            label: t`Tenant / Chain Code`,
            name: 'tenant',
            required: true
          },
          {
            type: 'text',
            label: t`Region`,
            name: 'region',
            required: true
          }
        ]
      },
      {
        id: 'loading',
        icon: Loading01Icon,
        type: 'loading',
        title: t`Waiting for Setup`,
        description: t`Since Oracle needs special handling of PMS integrations, it unfortunately takes a bit longer and needs manual setup from us. We should be able to setup your PMS at least in the next 48 hours. You will get an email from us as soon as we are done, you will then need to approve the integration in your PMS Cloud.`,
        fields: []
      },
      {
        id: 'verification',
        icon: Tick01Icon,
        type: 'button',
        title: t`Approval done`,
        description: t`When you approved us in your Oracle Cloud setup, please click this button so we can test the integration and start syncing you data, this can take a few minutes until it works.`,
        buttonLabel: t`I finished the approval`,
        fields: []
      }
    ],
    [GraphQL.Providers.Mews]: [
      {
        id: 'init',
        icon: Configuration01Icon,
        type: 'form',
        title: t`Initialize Mews Integration`,
        description: t`Mews has a quite simple and quick setup, we just need some information from you.`,
        fields: [
          {
            type: 'text',
            label: t`Access Token`,
            description: t`You should be able to access this token in Mews.`,
            placeholder: t`Example: C66EF7B239D24632943D115EDE9CB810-EA00F8FD8294692C940F6B5A8F9453D`,
            name: 'access_token',
            required: true
          }
        ]
      }
    ],
    [GraphQL.Providers.Apaleo]: [
      {
        id: 'init',
        icon: Configuration01Icon,
        type: 'popup_link',
        title: t`Initialize Apaleo Integration`,
        description: t`Apaleo has a simple and quick setup, you just need to connect GuestGoodie with Apaleo through clicking the button below.`,
        fields: [],
        linkTitle: t`Connect to Apaleo`,
        link: extractString('redirect_uri', data)
      },
      {
        id: 'properties',
        icon: ArrangeIcon,
        type: 'form',
        title: t`Select Property`,
        description: t`Select the property you want to connect with the current GuestGoodie Organization.`,
        fields: [
          {
            type: 'select',
            multi: false,
            label: t`Properties`,
            description: t`Select 1 property that you would like to sync.`,
            name: 'property',
            required: true,
            options: extractOptions('properties', data)
          }
        ]
      }
    ],
    [GraphQL.Providers.Stayntouch]: [
      {
        id: 'init',
        icon: Configuration01Icon,
        type: 'popup_link',
        title: t`Initialize StayNTouch Integration`,
        description: t`StayNTouch has a simple and quick setup, you just need to connect GuestGoodie with StayNTouch through clicking the button below.`,
        fields: [],
        linkTitle: t`Connect to StayNTouch`,
        link: extractString('redirect_uri', data)
      },
      {
        id: 'hotels',
        icon: ArrangeIcon,
        type: 'form',
        title: t`Select Hotel`,
        description: t`Select the hotel you want to connect with the current GuestGoodie Organization.`,
        fields: [
          {
            type: 'select',
            multi: false,
            label: t`Hotels`,
            description: t`Select 1 hotel that you would like to sync.`,
            name: 'hotel',
            required: true,
            options: extractOptions('hotels', data)
          }
        ]
      }
    ],
    [GraphQL.Providers.Tac]: [
      {
        id: 'init',
        icon: Configuration01Icon,
        type: 'form',
        title: t`Initialize TAC Integration`,
        description: t`First we need some information from your TAC account.`,
        fields: [
          {
            type: 'text',
            label: t`Username`,
            name: 'username',
            required: true
          },
          {
            type: 'text',
            inputType: 'password',
            label: t`Password`,
            name: 'password',
            required: false
          }
        ]
      }
    ]
  }

  return steps[provider]
}
